import { useRef, useState } from 'react';
import PlateConditionsFields, { EMPTY_CONDITIONS } from '../../PlateConditionsFields';
import { CHECK_STATUSES, CONTAMINATION_TYPES } from '../../../constants/plateChecks';
import { canDo } from '../stages';
import { checkBody, checkProblem, defaultPlateId, plateEditBody } from '../quickActions';
import { FormButtons, FormError, PlatePicker, grid, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';

/*
 * Log a check (a growth stage) on one of the isolate's plates, or -- with
 * `conditionsOnly` -- record what the incubator measured without changing
 * the plate's status (a check with no status, plate_checks.py:193).
 *
 * "Ready to transfer" needs where to: a plate or a slant set. A slant set
 * is created by the check itself (plate_checks.py:213), so the form says so
 * before it is sent.
 *
 * Lab writers (plates.py:552) also set the plate's target temperature and
 * notes here (PUT /plates/{id}); only a field the person edited, and only if
 * it changed, is sent.
 *
 * The check and the plate settings are two requests. If the check is
 * recorded and the settings fail, Save again retries the settings without
 * recording the same check a second time.
 */
export default function CheckForm({ item, plates, extra = {}, user, t, conditionsOnly = false, onDone, onCancel }) {
  const [plateId, setPlateId] = useState(extra.plateId || defaultPlateId(item, plates));
  const plate = plates.find((p) => p.plate_id === plateId) || null;
  const [form, setForm] = useState(() => ({
    status: extra.status || '', contamination_type: '', transfer_to: '', colony_diameter_mm: '',
    ph: '', humidity_pct: '', notes: '', ...EMPTY_CONDITIONS,
    temperature_c: plate?.temperature_c ?? '', plate_notes: plate?.notes ?? '',
  }));
  const { busy, error, setError, send } = useQuickSubmit(conditionsOnly ? 'hub-conditions' : 'hub-check');
  const canEditPlate = conditionsOnly && canDo(user, 'editPlate', item);
  // The fields the person edited; the plate settings send only those.
  const touched = useRef(new Set());
  // `${plateId} ${body}` of the check this form has already recorded.
  const recorded = useRef(null);
  const set = (key) => (e) => {
    touched.current.add(key);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const choosePlate = (id) => {
    setPlateId(id);
    touched.current = new Set();
    const next = plates.find((p) => p.plate_id === id);
    setForm((f) => ({ ...f, temperature_c: next?.temperature_c ?? '', plate_notes: next?.notes ?? '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const edit = canEditPlate ? plateEditBody(form, plate, touched.current) : {};
    const problem = checkProblem(form, { conditionsOnly });
    if (problem && !(problem === 'nothingToRecord' && Object.keys(edit).length)) {
      setError(t(`quick.problems.${problem}`));
      return;
    }
    const body = checkBody(conditionsOnly ? { ...form, status: '' } : form);
    const fingerprint = `${plateId} ${JSON.stringify(body)}`;
    if (Object.keys(body).length && recorded.current !== fingerprint) {
      const res = await send('post', `/plates/${plateId}/checks`, body);
      if (!res) return;
      recorded.current = fingerprint;
    }
    if (Object.keys(edit).length) {
      const res = await send('put', `/plates/${plateId}`, edit);
      if (!res) return;
    }
    onDone(conditionsOnly ? t('toast.conditionsSaved', { code: plate?.plate_code || '' })
      : t('toast.checkLogged', { code: plate?.plate_code || '' }));
  };

  if (!plates.length) return <p style={S.hint}>{t('quick.noPlates')}</p>;

  return (
    <form onSubmit={submit} style={stack} data-testid={conditionsOnly ? 'quick-conditions' : 'quick-check'}>
      <PlatePicker plates={plates} value={plateId} onChange={choosePlate} t={t} />
      {!conditionsOnly && (
        <>
          <label style={S.label}>
            {t('quick.check.status')} *
            <select value={form.status} onChange={set('status')} style={S.input} aria-required="true"
              title={t('tip.quick.status')} data-testid="quick-check-status">
              <option value="">{t('common:selectPlaceholder')}</option>
              {CHECK_STATUSES.map((s) => <option key={s} value={s}>{t(`plates:plateStatus.${s}`)}</option>)}
            </select>
          </label>
          {form.status === 'contaminated' && (
            <label style={S.label}>
              {t('quick.check.contamination')}
              <select value={form.contamination_type} onChange={set('contamination_type')} style={S.input}
                title={t('tip.quick.contamination')}>
                <option value="">—</option>
                {CONTAMINATION_TYPES.map((c) => <option key={c} value={c}>{t(`plates:contaminationType.${c}`)}</option>)}
              </select>
            </label>
          )}
          {form.status === 'ready_to_transfer' && (
            <>
              <label style={S.label}>
                {t('quick.check.transferTo')} *
                <select value={form.transfer_to} onChange={set('transfer_to')} style={S.input} aria-required="true"
                  title={t('tip.quick.transferTo')} data-testid="quick-check-transfer">
                  <option value="">{t('common:selectPlaceholder')}</option>
                  <option value="plate">{t('quick.check.toPlate')}</option>
                  <option value="slant">{t('quick.check.toSlant')}</option>
                </select>
              </label>
              {form.transfer_to === 'slant' && (
                <p role="note" style={S.warning} data-testid="quick-check-slant-warning">{t('quick.check.slantWarning')}</p>
              )}
            </>
          )}
        </>
      )}
      <div style={grid}>
        {!conditionsOnly && (
          <label style={S.label}>
            {t('quick.check.diameter')}
            <input type="number" min="0" step="0.1" value={form.colony_diameter_mm} onChange={set('colony_diameter_mm')}
              style={S.input} title={t('tip.quick.diameter')} />
          </label>
        )}
        <label style={S.label}>
          {t('quick.check.ph')}
          <input type="number" min="0" max="14" step="0.1" value={form.ph} onChange={set('ph')} style={S.input}
            title={t('tip.quick.ph')} data-testid="quick-check-ph" />
        </label>
        <label style={S.label}>
          {t('quick.check.humidity')}
          <input type="number" min="0" max="100" step="1" value={form.humidity_pct} onChange={set('humidity_pct')}
            style={S.input} title={t('tip.quick.humidity')} />
        </label>
        <PlateConditionsFields values={form} onChange={(v) => setForm((f) => ({ ...f, ...v }))}
          labelStyle={S.label} inputStyle={S.input} />
      </div>
      <label style={S.label}>
        {t('quick.notes')}
        <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
      </label>
      {canEditPlate && (
        <fieldset style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 10 }}>
          <legend style={S.hint}>{t('quick.check.plateSettings')}</legend>
          <div style={grid}>
            <label style={S.label}>
              {t('quick.check.targetTemp')}
              <input type="number" step="0.5" value={form.temperature_c} onChange={set('temperature_c')} style={S.input}
                title={t('tip.quick.targetTemp')} data-testid="quick-plate-temp" />
            </label>
          </div>
          <label style={{ ...S.label, marginTop: 8 }}>
            {t('quick.check.plateNotes')}
            <textarea rows={2} value={form.plate_notes} onChange={set('plate_notes')} style={S.input}
              title={t('tip.quick.plateNotes')} />
          </label>
        </fieldset>
      )}
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t}
        saveLabel={conditionsOnly ? t('quick.check.saveConditions') : t('quick.check.save')} />
    </form>
  );
}
